<template>
  <div v-if="hasPatients">
    <section aria-labelledby="mrn-search-heading">
      <v-row>
        <v-col cols="12">
          <h2 id="mrn-search-heading" class="text-h6 text-primary weight-600 d-flex align-center">
            <v-icon icon="mdi-account-search" class="mr-2" />
            Search Patient by Patient ID (MRN)
          </h2>
        </v-col>
      </v-row>
      <v-row align="center">
        <v-col cols="12" md="6">
          <v-text-field
            v-model="mrnSearch"
            label="Patient ID (MRN)"
            placeholder="Enter Patient ID (MRN) to search (exact match)"
            clearable
            @keyup.enter="searchPatients"
            @update:model-value="onMRNChange">
            <template #prepend-inner>
              <v-btn icon="mdi-magnify" variant="text" @click="searchPatients" />
            </template>
          </v-text-field>
        </v-col>
        <v-col cols="12" md="auto" class="d-flex align-center">
          <v-btn-lh @click="searchPatients">Search</v-btn-lh>
        </v-col>
      </v-row>
      <v-row v-if="searchLoading">
        <v-col cols="12">
          <v-progress-linear indeterminate class="mb-4" />
        </v-col>
      </v-row>
      <v-row v-if="searchResults.length > 0 || selectedPatient">
        <v-col cols="12">
          <v-card>
            <v-card-title class="text-h6 d-flex align-center justify-space-between">
              <span>{{
                selectedPatient
                  ? multiPatientChartEnabled
                    ? "Selected Patients"
                    : "Selected Patient"
                  : multiPatientChartEnabled
                  ? "Search Results - Select Patients"
                  : "Search Results - Select a Patient"
              }}</span>
              <v-btn-lh v-if="selectedPatient" variant="text" size="small" @click="clearSelection">
                Clear Selection
              </v-btn-lh>
            </v-card-title>
            <v-card-text>
              <!-- Multi-select mode: checkboxes -->
              <template v-if="multiPatientChartEnabled">
                <v-list lines="two" density="comfortable">
                  <v-list-item
                    v-for="patient in searchResults"
                    :key="patient.id"
                    :class="{ 'bg-primary-lighten-5': selectedPatientIds.includes(patient.id) }"
                    @click="onTogglePatient(patient)">
                    <template #prepend>
                      <v-checkbox
                        :model-value="selectedPatientIds.includes(patient.id)"
                        @update:model-value="onTogglePatient(patient)"
                        @click.stop />
                    </template>
                    <v-list-item-title> {{ patient.firstName }} {{ patient.lastName }} </v-list-item-title>
                    <v-list-item-subtitle>
                      <span v-if="patient.dob">{{ convertDateToMMDDYYYY(patient.dob) }}</span>
                      <span v-else>DOB: N/A</span>
                      <span v-if="patient.phoneNumber || patient.extraData?.phoneNumber">
                        • Phone: {{ patient.phoneNumber || patient.extraData?.phoneNumber }}
                      </span>
                      <span v-else> • Phone: N/A</span>
                      <span v-if="patient.address"> • {{ patient.address }}</span>
                      <span v-if="patient.externalId"> • Patient ID (MRN): {{ patient.externalId }}</span>
                    </v-list-item-subtitle>
                  </v-list-item>
                </v-list>
              </template>
              <!-- Single-select mode: radio buttons (existing behavior) -->
              <template v-else>
                <v-radio-group v-model="selectedPatientId" @update:model-value="onPatientSelectionChange">
                  <v-list lines="two" density="comfortable">
                    <v-list-item
                      v-for="patient in searchResults"
                      :key="patient.id"
                      :class="{ 'bg-primary-lighten-5': selectedPatientId === patient.id }"
                      @click="onSelectPatient(patient)">
                      <template #prepend>
                        <v-radio :value="patient.id" />
                      </template>
                      <v-list-item-title> {{ patient.firstName }} {{ patient.lastName }} </v-list-item-title>
                      <v-list-item-subtitle>
                        <span v-if="patient.dob">{{ convertDateToMMDDYYYY(patient.dob) }}</span>
                        <span v-else>DOB: N/A</span>
                        <span v-if="patient.phoneNumber || patient.extraData?.homePhone">
                          • Phone: {{ patient.phoneNumber || patient.extraData?.homePhone }}
                        </span>
                        <span v-else> • Phone: N/A</span>
                        <span v-if="patient.address"> • {{ patient.address }}</span>
                        <span v-if="patient.externalId"> • Patient ID (MRN): {{ patient.externalId }}</span>
                      </v-list-item-subtitle>
                    </v-list-item>
                  </v-list>
                </v-radio-group>
              </template>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <v-row v-else-if="hasSearched && !searchLoading">
        <v-col cols="12">
          <v-alert type="info" variant="tonal">No patients found matching the Patient ID (MRN).</v-alert>
        </v-col>
      </v-row>
      <!-- Selected patient cards (multi-select mode) -->
      <v-row v-if="multiPatientChartEnabled && selectedPatients.length > 0">
        <v-col cols="12">
          <div class="d-flex flex-wrap ga-3">
            <v-card
              v-for="patient in selectedPatients"
              :key="patient.id"
              variant="outlined"
              class="pa-3"
              :class="{ 'border-primary': patient.id === selectedPatient?.id }"
              min-width="250">
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="text-subtitle-1 font-weight-bold">{{ patient.firstName }} {{ patient.lastName }}</div>
                  <div class="text-body-2 text-medium-emphasis">
                    <span v-if="patient.dob">DOB: {{ convertDateToMMDDYYYY(patient.dob) }}</span>
                    <span v-if="patient.externalId"> | Patient ID (MRN): {{ patient.externalId }}</span>
                  </div>
                </div>
                <v-chip v-if="patient.id === selectedPatient?.id" size="small" color="primary" variant="tonal">
                  Primary
                </v-chip>
              </div>
            </v-card>
          </div>
        </v-col>
      </v-row>
      <v-row v-if="intakeRecordsLoading || importedOrdersLoading">
        <v-col cols="12">
          <v-progress-linear indeterminate class="mb-4" />
        </v-col>
      </v-row>
      <!-- Source picker: only asked when this patient has both imported order data and usable
           intake documents — otherwise the single available source proceeds automatically. -->
      <v-row
        v-if="
          selectedPatient &&
          !intakeRecordsLoading &&
          !importedOrdersLoading &&
          hasImportedOrders &&
          hasUsableIntakeMedications
        ">
        <v-col cols="12">
          <v-alert type="info" variant="tonal" class="mb-2">
            This patient has both imported order data and reviewed intake documents. Choose which one to create this
            submission from.
          </v-alert>
          <v-btn-toggle v-model="submissionSource" mandatory color="primary" divided>
            <v-btn value="import">From Imported Order Data</v-btn>
            <v-btn value="intake">From Intake Documents</v-btn>
          </v-btn-toggle>
        </v-col>
      </v-row>
      <!-- True dead end only: no import rows AND no documents. Medication-less documents are
           still attachable below (LAM-5022). -->
      <v-row
        v-if="
          selectedPatient &&
          !intakeRecordsLoading &&
          !importedOrdersLoading &&
          !hasImportedOrders &&
          !hasIntakeDocuments
        ">
        <v-col cols="12">
          <v-alert type="info" variant="tonal">
            No imported order or intake data found for this patient. You can still start from the patient list:
            demographics, plus the insurance card you select below (if any), and fill in the rest by hand.
          </v-alert>
          <v-progress-linear v-if="patientInsurancesLoading" indeterminate class="mt-3" />
          <template v-else-if="patientInsurances.length > 0">
            <div class="d-flex align-center justify-space-between mt-3">
              <p class="text-subtitle-2 mb-0">Insurance on file for this patient</p>
              <v-btn-lh
                v-if="selectedPatientInsuranceId"
                variant="text"
                size="small"
                @click="selectedPatientInsuranceId = null">
                Clear Selection
              </v-btn-lh>
            </div>
            <v-radio-group v-model="selectedPatientInsuranceId" hide-details>
              <v-list lines="two" density="compact">
                <v-list-item
                  v-for="card in patientInsurances"
                  :key="card.id"
                  style="cursor: pointer"
                  @click="selectedPatientInsuranceId = card.id">
                  <template #prepend>
                    <v-radio :value="card.id" @click.stop="selectedPatientInsuranceId = card.id" />
                  </template>
                  <v-list-item-title>{{
                    card.insuranceProvider || card.memberName || "Insurance card"
                  }}</v-list-item-title>
                  <v-list-item-subtitle>{{ patientInsuranceSubtitle(card) }}</v-list-item-subtitle>
                </v-list-item>
              </v-list>
            </v-radio-group>
          </template>
          <v-btn
            class="mt-3"
            :loading="usingPatientDetails"
            :disabled="patientInsurancesLoading"
            @click="handleUsePatientDetails">
            Start New Prior Auth from Patient Details
          </v-btn>
        </v-col>
      </v-row>
      <!-- Imported order data (e.g. Brooks CSV import): pick which row to prepopulate the
           standard form from. Everything from here on is edited in the SAME rich form the
           intake path uses — this is only a source selector, not a data-entry step. -->
      <v-row v-if="selectedPatient && submissionSource === 'import' && hasImportedOrders">
        <v-col cols="12">
          <v-card>
            <v-card-title class="text-h6">Imported Order Data</v-card-title>
            <v-card-text>
              <v-radio-group v-model="selectedImportRowId">
                <v-list lines="three" density="comfortable">
                  <v-list-item
                    v-for="row in importedOrders"
                    :key="row.importRowId"
                    style="cursor: pointer"
                    @click="selectedImportRowId = row.importRowId">
                    <template #prepend>
                      <v-radio :value="row.importRowId" @click.stop="selectedImportRowId = row.importRowId" />
                    </template>
                    <v-list-item-title>{{ row.fields.medication }}</v-list-item-title>
                    <v-list-item-subtitle>
                      <span v-if="row.faxFormName"> Fax form: {{ row.faxFormName }}</span>
                      <v-alert
                        v-if="row.existingOrderKey"
                        type="warning"
                        variant="tonal"
                        density="compact"
                        class="mt-1">
                        A PA already exists for this order ({{ row.existingOrderKey }}, {{ row.existingOrderStatus }}).
                        Creating another is allowed, but double-check this isn't a duplicate before continuing.
                      </v-alert>
                    </v-list-item-subtitle>
                  </v-list-item>
                </v-list>
              </v-radio-group>

              <template v-if="selectedImportRow">
                <v-divider class="mb-4" />
                <p class="text-caption text-medium-emphasis mb-2">
                  Structured fax-form information (not editable here — used as-is when the fax form is generated):
                </p>
                <v-list density="compact">
                  <v-list-item v-for="(value, key) in selectedImportRow.fields.extraStructuredInformation" :key="key">
                    <v-list-item-title class="text-caption">{{ key }}: {{ value }}</v-list-item-title>
                  </v-list-item>
                </v-list>
                <!-- LAM-4720: placed before the button (not as a separate card after it) so it's
                     seen and actionable before the user commits — Brooks' own auto-attached
                     patientChart (from classified Referral/Renewal/Labs documents) may not cover
                     everything on file for this patient. Checkboxes only — no insurance/medication
                     pickers here, since the imported row already supplies those. -->
                <template v-if="!intakeRecordsLoading && intakeRecords?.documents?.length > 0">
                  <v-divider class="mb-4" />
                  <p class="text-subtitle-2 mb-1">Attach Intake Documents (optional)</p>
                  <p class="text-caption text-medium-emphasis mb-2">
                    Select any additional intake documents on file for this patient to attach as supporting documents.
                  </p>
                  <v-list lines="two" density="comfortable">
                    <v-list-item
                      v-for="doc in intakeRecords.documents"
                      :key="doc.id"
                      style="cursor: pointer"
                      @click="toggleDocumentSelection(doc.id, !selectedDocuments.includes(doc.id))">
                      <template #prepend>
                        <v-checkbox
                          :model-value="selectedDocuments.includes(doc.id)"
                          @update:model-value="toggleDocumentSelection(doc.id, $event)"
                          @click.stop />
                      </template>
                      <v-list-item-title>
                        <span v-if="doc.documentTypes && doc.documentTypes.length > 0">{{
                          doc.documentTypes.join(", ")
                        }}</span>
                        <span v-else>Intake Document</span>
                      </v-list-item-title>
                      <v-list-item-subtitle v-if="doc.createdOn">
                        Received on {{ new Date(doc.createdOn).toLocaleString() }}
                      </v-list-item-subtitle>
                      <template #append>
                        <v-btn-lh
                          v-if="doc.inputPresignedUrl"
                          variant="text"
                          size="small"
                          @click.stop="openPreview(doc)">
                          Quick Look
                        </v-btn-lh>
                      </template>
                    </v-list-item>
                  </v-list>
                </template>
                <v-btn-lh class="mt-4" @click="handleUseImportedOrder">
                  Start New Prior Auth with Selected Data
                </v-btn-lh>
              </template>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <v-row v-if="selectedPatient && !intakeRecordsLoading && intakeRecords && submissionSource === 'intake'">
        <v-col cols="12">
          <v-card>
            <v-card-title class="text-h6">Intake Records</v-card-title>
            <v-card-text>
              <div v-if="intakeRecords.documents && intakeRecords.documents.length > 0">
                <div v-for="doc in intakeRecords.documents" :key="doc.id" class="mb-3">
                  <div
                    class="d-flex align-center justify-space-between"
                    style="cursor: pointer"
                    @click="toggleDocumentSelection(doc.id, !selectedDocuments.includes(doc.id))">
                    <div class="d-flex align-center">
                      <v-checkbox
                        :model-value="selectedDocuments.includes(doc.id)"
                        class="mr-3"
                        @update:model-value="toggleDocumentSelection(doc.id, $event)"
                        @click.stop />
                      <div class="text-subtitle-1 d-flex align-center mb-6">
                        <span v-if="doc.documentTypes && doc.documentTypes.length > 0">
                          <span v-for="(docType, index) in doc.documentTypes" :key="index">
                            <span v-if="index > 0">, </span>{{ docType }}
                          </span>
                          <span v-if="doc.createdOn"> received on {{ new Date(doc.createdOn).toLocaleString() }}</span>
                        </span>
                        <span v-else-if="doc.createdOn">
                          Document received at {{ new Date(doc.createdOn).toLocaleString() }}
                        </span>
                        <span v-else>Document</span>
                      </div>
                    </div>
                    <v-btn-lh
                      v-if="doc.inputPresignedUrl"
                      class="mb-6"
                      variant="text"
                      size="small"
                      @click.stop="openPreview(doc)">
                      Quick Look
                    </v-btn-lh>
                  </div>
                  <!-- Insurance Cards for this document (Max 1 Selection) -->
                  <div v-if="doc.insuranceCards && doc.insuranceCards.length > 0" class="ml-8">
                    <div class="d-flex align-center justify-space-between">
                      <p class="text-subtitle-2 mb-0">Select Insurance</p>
                      <v-btn-lh
                        v-if="selectedInsuranceCards[doc.id]"
                        variant="text"
                        size="small"
                        @click="clearInsuranceSelection(doc.id)">
                        Clear Selection
                      </v-btn-lh>
                    </div>
                    <v-radio-group
                      :model-value="selectedInsuranceCards[doc.id]"
                      @update:model-value="selectedInsuranceCards[doc.id] = $event">
                      <v-list lines="two" density="compact">
                        <v-list-item
                          v-for="card in doc.insuranceCards"
                          :key="card.id"
                          style="cursor: pointer"
                          @click="handleInsuranceClick(doc.id, card.id, $event)">
                          <template #prepend>
                            <v-radio :value="card.id" @click.stop="handleInsuranceClick(doc.id, card.id, $event)" />
                          </template>
                          <v-list-item-title>{{ card.payorName || "Insurance Card" }}</v-list-item-title>
                          <v-list-item-subtitle>
                            <span v-if="card.memberId"> • <strong>Member ID:</strong> {{ card.memberId }}</span>
                            <span v-if="card.rxBin"> • <strong>BIN:</strong> {{ card.rxBin }}</span>
                            <span v-if="card.rxPcn"> • <strong>PCN:</strong> {{ card.rxPcn }}</span>
                            <span v-if="card.rxGrp || card.Group">
                              • <strong>Group:</strong> {{ card.rxGrp ? card.rxGrp : card.Group }}
                            </span>
                            <span v-if="card.insurancePlan"> • <strong>Plan:</strong> {{ card.insurancePlan }}</span>
                            <span v-if="card.benefitType"> • <strong>Type:</strong> {{ card.benefitType }}</span>
                          </v-list-item-subtitle>
                        </v-list-item>
                      </v-list>
                    </v-radio-group>
                  </div>
                  <v-alert v-else type="info" variant="tonal" class="ml-8">
                    No insurance cards found for this document.
                  </v-alert>
                  <!-- Medications for this document (Max 1 Selection) -->
                  <div v-if="getDocumentMedications(doc).length > 0" class="ml-8">
                    <div class="d-flex align-center justify-space-between">
                      <p class="text-subtitle-2 mb-0">Select Medication</p>
                      <v-btn-lh
                        v-if="selectedMedications[doc.id]"
                        variant="text"
                        size="small"
                        @click="clearMedicationSelection(doc.id)">
                        Clear Selection
                      </v-btn-lh>
                    </div>
                    <v-radio-group
                      :model-value="selectedMedications[doc.id]"
                      @update:model-value="selectedMedications[doc.id] = $event">
                      <v-list lines="two" density="compact">
                        <v-list-item
                          v-for="therapy in getDocumentMedications(doc)"
                          :key="therapy.name"
                          style="cursor: pointer"
                          @click="handleMedicationClick(doc.id, therapy.name, $event)">
                          <template #prepend>
                            <v-radio
                              :value="therapy.name"
                              @click.stop="handleMedicationClick(doc.id, therapy.name, $event)" />
                          </template>
                          <v-list-item-title>
                            {{ therapy.name || "Therapy Administration" }}
                          </v-list-item-title>
                          <v-list-item-subtitle>
                            <span v-if="therapy.dosage"> • <strong>Dosage:</strong> {{ therapy.dosage }}</span>
                            <span v-if="therapy.frequency || (therapy.frequencyValue && therapy.frequencyUnit)">
                              • <strong>Frequency:</strong>
                              {{
                                therapy.frequency
                                  ? therapy.frequency
                                  : therapy.frequencyValue + " " + therapy.frequencyUnit
                              }}
                            </span>
                            <span v-if="therapy.notes"> • <strong>Notes:</strong> {{ therapy.notes }}</span>
                          </v-list-item-subtitle>
                        </v-list-item>
                      </v-list>
                    </v-radio-group>
                  </div>
                  <v-alert v-else type="info" variant="tonal" class="ml-8">
                    No medications found for this document.
                  </v-alert>
                </div>
              </div>
              <div
                v-if="
                  intakeRecords.documents?.length > 0 &&
                  intakeRecords.documents.length < (intakeRecords.totalCount ?? 0)
                "
                class="mt-3">
                <v-btn variant="outlined" :loading="intakeRecordsLoadingMore" @click="loadMoreIntakeRecords">
                  Load more (showing {{ intakeRecords.documents.length }} of {{ intakeRecords.totalCount }})
                </v-btn>
              </div>
              <v-alert v-else-if="!intakeRecords.documents?.length" type="info" variant="tonal">
                No documents found for this patient.
              </v-alert>
            </v-card-text>
            <v-btn
              v-if="
                submissionSource === 'intake' &&
                (Object.keys(selectedMedications).length > 0 ||
                  Object.keys(selectedInsuranceCards).length > 0 ||
                  selectedDocuments.length > 0)
              "
              class="ma-4"
              @click="handlePrepopulate">
              Start New Prior Auth with Selected Data
            </v-btn>
          </v-card>
        </v-col>
      </v-row>
    </section>

    <v-dialog v-model="showPreviewDialog" max-width="90%" width="90%" height="90%">
      <v-card>
        <v-card-title class="d-flex align-center justify-space-between">
          <span>Document Preview</span>
          <v-btn icon="mdi-close" variant="text" @click="closePreview" />
        </v-card-title>
        <v-card-text style="height: 80vh; padding: 0; position: relative">
          <v-progress-linear v-if="previewLoading" indeterminate class="mb-0" />
          <object
            v-if="previewDocumentUrl && !previewLoading"
            :data="previewDocumentUrl"
            type="application/pdf"
            style="width: 100%; height: 100%; border: none">
            <iframe :src="previewDocumentUrl" style="width: 100%; height: 100%; border: none" frameborder="0" />
            <p>
              Your browser does not support PDFs. <a :href="previewDocumentUrl" target="_blank">Download the PDF</a>.
            </p>
          </object>
          <v-alert v-if="previewError" type="error" variant="tonal" class="ma-4">
            Failed to load document preview. Please try again.
          </v-alert>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { intakeService } from "@/services/intakeService";
import { patientService } from "@/services/patientService";
import { insuranceCardOrderFields } from "@/utils/patientInsurance";
import { convertDateToMMDDYYYY, fetchDiagnosisNameFromICD10 } from "@/utils";
import { usaStatesListItems } from "@/utils/usaStates";
import { mainServices } from "@/services/mainServices";
import { getMedicationDefaultsForName, applyMedicationDefaultsToTarget } from "@/utils/clientConfig";
import { useRequirementsStore } from "@/stores";

const emit = defineEmits(["prepopulate-order-info"]);
const requirementsStore = useRequirementsStore();

const mrnSearch = ref("");
const searchResults = ref([]);
const searchLoading = ref(false);
const hasSearched = ref(false);
const selectedPatient = ref(null);
const selectedPatientId = ref(null);
const intakeRecords = ref(null);
const intakeRecordsLoading = ref(false);
const selectedDocuments = ref([]);
const selectedInsuranceCards = ref({}); // { documentId: insuranceCardId }
// Patient-level insurance cards (PatientInsurance) for the no-records fallback. Fetched alongside the
// intake/import sources; the user picks one (a lone card is pre-selected) or proceeds without.
const patientInsurances = ref([]);
const patientInsurancesLoading = ref(false);
const selectedPatientInsuranceId = ref(null);
const PATIENT_INSURANCE_BENEFIT_LABELS = { pharmacy_benefits: "Pharmacy", medical_benefits: "Medical" };
const selectedMedications = ref({}); // { orderFormId: [therapyAdministrationId1, therapyAdministrationId2, ...] }
const showPreviewDialog = ref(false);
const previewDocumentUrl = ref(null);
const previewLoading = ref(false);
const previewError = ref(false);
const hasPatients = ref(false);

// Imported order data (e.g. Brooks CSV import) — an alternate source of order data for patients
// whose order lives in PriorAuthOrderImport rather than intake-document extraction (LAM-4720).
const importedOrders = ref([]);
const importedOrdersLoading = ref(false);
const selectedImportRowId = ref(null);
// Explicit user choice when a patient has both usable intake data and imported order rows —
// never guessed, since the two paths produce meaningfully different submissions.
const submissionSource = ref(null); // "import" | "intake" | null

// Multi-patient chart support
const multiPatientChartEnabled = computed(
  () => requirementsStore.clientConfig?.intake?.multiPatientChartEnabled ?? false
);
const selectedPatients = ref([]); // Array of selected patient objects (multi-select mode)
const selectedPatientIds = ref([]); // Array of selected patient IDs (multi-select mode)

// Check if the client company has any patients in the database
// Uses count endpoint to determine if patient search should be shown
const checkIfPatientsExist = async () => {
  try {
    const response = await patientService.getPatientsCount();
    hasPatients.value = (response.count || 0) > 0;
  } catch (error) {
    // If check fails, default to hiding the component to be safe
    hasPatients.value = false;
  }
};

onMounted(async () => {
  await checkIfPatientsExist();
});

const onMRNChange = () => {
  if (!mrnSearch.value) {
    searchResults.value = [];
    hasSearched.value = false;
    clearSelection();
  }
};

const searchPatients = async () => {
  if (!mrnSearch.value?.trim()) {
    return;
  }

  clearSelection();
  searchResults.value = [];
  searchLoading.value = true;
  hasSearched.value = true;
  try {
    const response = await patientService.searchPatientsByMRN(mrnSearch.value.trim());
    searchResults.value = response?.results || [];
  } catch (error) {
    searchResults.value = [];
  } finally {
    searchLoading.value = false;
  }
};

const intakeRecordsLoadingMore = ref(false);

// Stale-response guard: a slow fetch for patient A must not land under patient B. Commit only
// if this is still the newest request for the still-selected patient; loading flags key on the
// token alone so a superseded request can't clear the newer one's spinner.
let intakeRecordsRequestToken = 0;
let importedOrdersRequestToken = 0;

const fetchIntakeRecords = async (patientId, options = {}) => {
  const { page = 1, append = false } = options;
  if (!patientId) {
    intakeRecords.value = null;
    return;
  }
  const token = ++intakeRecordsRequestToken;
  const isCurrentRequest = () => token === intakeRecordsRequestToken && selectedPatientId.value === patientId;

  if (append) {
    intakeRecordsLoadingMore.value = true;
  } else {
    intakeRecordsLoading.value = true;
  }
  try {
    const response = await intakeService.getPatientIntakeRecords(patientId, {
      page,
      pageSize: 5,
    });
    if (!isCurrentRequest()) return;
    const data = response || {
      documents: [],
      totalCount: 0,
      page: 1,
      pageSize: 5,
    };
    const docs = data.documents || [];
    if (append && intakeRecords.value?.documents) {
      intakeRecords.value = {
        ...intakeRecords.value,
        documents: [...intakeRecords.value.documents, ...docs],
        totalCount: data.totalCount ?? intakeRecords.value.totalCount,
        page: data.page ?? page,
        pageSize: data.pageSize ?? intakeRecords.value.pageSize,
      };
    } else {
      intakeRecords.value = {
        documents: docs,
        totalCount: data.totalCount ?? 0,
        page: data.page ?? 1,
        pageSize: data.pageSize ?? 5,
      };
    }
  } catch (error) {
    if (!isCurrentRequest()) return;
    if (!append) {
      intakeRecords.value = {
        documents: [],
        totalCount: 0,
        page: 1,
        pageSize: 5,
      };
    }
  } finally {
    if (token === intakeRecordsRequestToken) {
      intakeRecordsLoading.value = false;
      intakeRecordsLoadingMore.value = false;
    }
  }
};

const loadMoreIntakeRecords = () => {
  if (!selectedPatientId.value || !intakeRecords.value) return;
  const nextPage = (intakeRecords.value.page ?? 1) + 1;
  if (intakeRecords.value.documents?.length >= (intakeRecords.value.totalCount ?? 0)) return;
  fetchIntakeRecords(selectedPatientId.value, { page: nextPage, append: true });
};

const fetchImportedOrders = async (patientId) => {
  if (!patientId) {
    importedOrders.value = [];
    return;
  }
  const token = ++importedOrdersRequestToken;
  const isCurrentRequest = () => token === importedOrdersRequestToken && selectedPatientId.value === patientId;

  importedOrdersLoading.value = true;
  try {
    const response = await intakeService.getPatientImportedOrders(patientId);
    if (!isCurrentRequest()) return;
    importedOrders.value = Array.isArray(response) ? response : [];
  } catch (error) {
    if (!isCurrentRequest()) return;
    importedOrders.value = [];
  } finally {
    if (token === importedOrdersRequestToken) {
      importedOrdersLoading.value = false;
    }
  }
};

let patientInsurancesRequestToken = 0;
const fetchPatientInsurances = async (patientId) => {
  if (!patientId) {
    patientInsurances.value = [];
    return;
  }
  const token = ++patientInsurancesRequestToken;
  const isCurrentRequest = () => token === patientInsurancesRequestToken && selectedPatientId.value === patientId;
  // Drop the previous patient's cards BEFORE fetching (the watcher below then clears the selected
  // id too): the dead-end panel can render as soon as the intake/import fetches settle, and a card
  // left over from patient A must never ride onto patient B's form.
  patientInsurances.value = [];
  patientInsurancesLoading.value = true;
  try {
    const response = await patientService.getPatientInsurances(patientId);
    if (!isCurrentRequest()) return;
    patientInsurances.value = Array.isArray(response) ? response : [];
  } catch (error) {
    if (!isCurrentRequest()) return;
    // Coverage is a convenience for the fallback; the demographics alone are still worth starting from.
    patientInsurances.value = [];
  } finally {
    if (token === patientInsurancesRequestToken) {
      patientInsurancesLoading.value = false;
    }
  }
};

// A lone card is the obvious choice, so it is pre-selected; with several the user picks — the
// server never guesses which card a submission meant (same rule as the form's own picker).
watch(patientInsurances, (cards) => {
  selectedPatientInsuranceId.value = cards.length === 1 ? cards[0].id : null;
});
const selectedPatientInsurance = computed(
  () => patientInsurances.value.find((card) => card.id === selectedPatientInsuranceId.value) || null
);
const patientInsuranceSubtitle = (card) =>
  [
    card.memberId ? `Member ID: ${card.memberId}` : null,
    card.groupNumber ? `Group: ${card.groupNumber}` : null,
    card.rxBin ? `BIN: ${card.rxBin}` : null,
    card.rxPcn ? `PCN: ${card.rxPcn}` : null,
    PATIENT_INSURANCE_BENEFIT_LABELS[card.benefitType] || null,
  ]
    .filter(Boolean)
    .join(" • ");

const hasImportedOrders = computed(() => importedOrders.value.length > 0);

// Mirrors the per-document "No medications found" check rendered above. Gates the source
// picker only — medication-less documents are still attachable, see `hasIntakeDocuments`.
const hasUsableIntakeMedications = computed(() =>
  (intakeRecords.value?.documents || []).some((doc) => getDocumentMedications(doc).length > 0)
);

// Weaker on purpose: a medication-less document can't prepopulate a submission but is still
// worth attaching to a manually-filled one (LAM-5022).
const hasIntakeDocuments = computed(() => (intakeRecords.value?.documents || []).length > 0);

const selectedImportRow = computed(
  () => importedOrders.value.find((row) => row.importRowId === selectedImportRowId.value) || null
);

// Once both sources are known for this patient, only ask the user to pick when both are
// actually usable — otherwise the single available source proceeds with no extra friction.
// The intake fallthrough keys on documents, not medications: with no import rows, gating it on
// medications hid the document list and left those users a dead-end warning (LAM-5022).
watch(
  [hasImportedOrders, hasUsableIntakeMedications, hasIntakeDocuments],
  ([importAvailable, medicationsAvailable, documentsAvailable]) => {
    if (importAvailable && medicationsAvailable) {
      submissionSource.value = null;
    } else if (importAvailable) {
      submissionSource.value = "import";
    } else if (documentsAvailable) {
      submissionSource.value = "intake";
    } else {
      submissionSource.value = null;
    }
  }
);

// Pre-check (not force) whatever the client's own classification hook would have auto-attached
// (e.g. Brooks' Referral/Renewal/Labs matcher) — a recommended default the user can freely
// uncheck, never a hidden guarantee. Nothing is attached outside `selectedDocuments` (LAM-4720).
watch(selectedImportRow, (row) => {
  selectedDocuments.value = row?.autoSelectedIntakeDocumentIds ? [...row.autoSelectedIntakeDocumentIds] : [];
});

/**
 * Prepopulate the standard form from the selected imported-order row, exactly like
 * `prepopulateOrderInfo()` does for intake data — every field the backend builder produced
 * (patient/provider/medication/diagnosis) flows through the SAME `prepopulate-order-info` event
 * into the rich, fully-editable wizard form. No separate creation step here — creation happens
 * through the normal wizard afterward, same as intake-sourced submissions, so field edits (e.g.
 * correcting `medication` or `insuranceProvider`) are re-derived server-side at that point rather
 * than requiring a bespoke override/recompute step in this component.
 *
 * `patientChart` is built ENTIRELY from `selectedDocuments` — exactly and only what's checked in
 * the "Attach Intake Documents" section, including whatever was pre-checked by the watcher above.
 * Nothing is force-attached: unchecking a pre-checked (auto-classified) document excludes it,
 * same as any manually-added one (LAM-4720).
 */
const handleUseImportedOrder = () => {
  const row = selectedImportRow.value;
  if (!row) return;

  const prepopulatedData = { ...row.fields };
  if (selectedPatient.value?.externalId) {
    prepopulatedData.patientExternalId = selectedPatient.value.externalId;
  }
  // Set immediately from the backend's own resolution (same function _create_submission uses
  // at save time) so submission-method-gated form fields (e.g. CMM-only required fields) show
  // correctly from the start, instead of only becoming right once the order is actually saved.
  if (row.submissionType) {
    prepopulatedData.submissionMethod = row.submissionType;
  }
  prepopulatedData.patientChart = (intakeRecords.value?.documents || [])
    .filter((doc) => selectedDocuments.value.includes(doc.id))
    .map(buildPatientChartEntryFromIntakeDoc)
    .filter(Boolean);
  // Second arg = whose data this is, so the handler can tell a patient switch (clear the
  // user-entered medication queues) from a same-patient re-prepopulate (keep them — no
  // prepopulate source can ever restore those queues). Captured before clearSelections().
  emit("prepopulate-order-info", prepopulatedData, selectedPatientId.value);
  clearSelections();
};

const onSelectPatient = (patient) => {
  selectedPatientId.value = patient.id;
  selectedPatient.value = patient;
};

const onPatientSelectionChange = (patientId) => {
  const patient = searchResults.value.find((p) => p.id === patientId);
  if (patient) {
    selectedPatient.value = patient;
  }
};

const onTogglePatient = (patient) => {
  const index = selectedPatientIds.value.indexOf(patient.id);
  if (index >= 0) {
    // Deselecting
    selectedPatientIds.value.splice(index, 1);
    selectedPatients.value = selectedPatients.value.filter((p) => p.id !== patient.id);
    // If deselecting the primary (first), promote next one or clear
    if (selectedPatient.value?.id === patient.id) {
      if (selectedPatients.value.length > 0) {
        selectedPatient.value = selectedPatients.value[0];
        selectedPatientId.value = selectedPatients.value[0].id;
      } else {
        selectedPatient.value = null;
        selectedPatientId.value = null;
      }
    }
  } else {
    // Selecting
    selectedPatientIds.value.push(patient.id);
    selectedPatients.value.push(patient);
    // First selected becomes primary
    if (!selectedPatient.value) {
      selectedPatient.value = patient;
      selectedPatientId.value = patient.id;
    }
  }
};

const clearSelection = () => {
  selectedPatient.value = null;
  selectedPatientId.value = null;
  selectedPatients.value = [];
  selectedPatientIds.value = [];
  intakeRecords.value = null;
  importedOrders.value = [];
  patientInsurances.value = [];
  selectedImportRowId.value = null;
  submissionSource.value = null;
  clearSelections();
};

/** Reset search terms and results (used when starting a new submission). */
const resetSearch = () => {
  mrnSearch.value = "";
  searchResults.value = [];
  hasSearched.value = false;
  clearSelection();
};

const clearSelections = () => {
  selectedDocuments.value = [];
  selectedInsuranceCards.value = {};
  selectedMedications.value = {};
};

const toggleDocumentSelection = (docId, isSelected) => {
  if (isSelected) {
    if (!selectedDocuments.value.includes(docId)) {
      selectedDocuments.value.push(docId);
    }
  } else {
    selectedDocuments.value = selectedDocuments.value.filter((id) => id !== docId);
    // Clear selections for this document
    delete selectedInsuranceCards.value[docId];
    delete selectedMedications.value[docId];
  }
};

/** Shared with `prepopulateOrderInfo()` (intake path) and `handleUseImportedOrder()` (import
 * path, LAM-4720) so a checked intake document produces the same patientChart entry shape
 * regardless of which source flow attaches it. */
const buildPatientChartEntryFromIntakeDoc = (doc) => {
  const label = doc.key
    ? `${doc.key} - ${doc.createdOn ? new Date(doc.createdOn).toLocaleString() : "Intake Document"}`
    : `Intake Document - ${doc.createdOn ? new Date(doc.createdOn).toLocaleString() : ""}`;
  const links = doc.inputPresignedUrl ? [doc.inputPresignedUrl] : [];
  if (links.length === 0 && !doc.key) return null;
  return { label, links };
};

const getDocumentMedications = (doc) => {
  const medications = [];
  if (doc.orderForms && doc.orderForms.length > 0) {
    doc.orderForms.forEach((form) => {
      if (form.therapyAdministration && form.therapyAdministration.length > 0) {
        medications.push(...form.therapyAdministration);
      }
    });
  }
  return medications;
};

const handleInsuranceClick = (docId, cardId, event) => {
  // If clicking the same card that's already selected, clear the selection
  const currentValue = selectedInsuranceCards.value[docId];
  if (currentValue === cardId) {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    clearInsuranceSelection(docId);
  } else {
    // Select the card
    selectedInsuranceCards.value[docId] = cardId;
  }
};

const handleMedicationClick = (docId, therapyName, event) => {
  // If clicking the same medication that's already selected, clear the selection
  const currentValue = selectedMedications.value[docId];
  if (currentValue === therapyName) {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    clearMedicationSelection(docId);
  } else {
    // Select the medication
    selectedMedications.value[docId] = therapyName;
  }
};

const clearInsuranceSelection = (docId) => {
  delete selectedInsuranceCards.value[docId];
};

const clearMedicationSelection = (docId) => {
  delete selectedMedications.value[docId];
};

const openPreview = async (doc) => {
  if (!doc.id) {
    return;
  }

  showPreviewDialog.value = true;
  previewLoading.value = true;
  previewError.value = false;
  previewDocumentUrl.value = null;

  try {
    // Fetch PDF as blob following codebase pattern (handles auth automatically)
    const response = await intakeService.getDocumentPreview(doc.id);
    const pdfBlob = new Blob([response.data], { type: "application/pdf" });
    previewDocumentUrl.value = URL.createObjectURL(pdfBlob);
  } catch (error) {
    previewError.value = true;
  } finally {
    previewLoading.value = false;
  }
};

const closePreview = () => {
  showPreviewDialog.value = false;
  if (previewDocumentUrl.value) {
    URL.revokeObjectURL(previewDocumentUrl.value);
  }
  previewDocumentUrl.value = null;
  previewError.value = false;
};

// State abbreviation to full name mapping
const stateAbbrevToFullName = {
  AL: "Alabama",
  AK: "Alaska",
  AZ: "Arizona",
  AR: "Arkansas",
  CA: "California",
  CO: "Colorado",
  CT: "Connecticut",
  DE: "Delaware",
  FL: "Florida",
  GA: "Georgia",
  HI: "Hawaii",
  ID: "Idaho",
  IL: "Illinois",
  IN: "Indiana",
  IA: "Iowa",
  KS: "Kansas",
  KY: "Kentucky",
  LA: "Louisiana",
  ME: "Maine",
  MD: "Maryland",
  MA: "Massachusetts",
  MI: "Michigan",
  MN: "Minnesota",
  MS: "Mississippi",
  MO: "Missouri",
  MT: "Montana",
  NE: "Nebraska",
  NV: "Nevada",
  NH: "New Hampshire",
  NJ: "New Jersey",
  NM: "New Mexico",
  NY: "New York",
  NC: "North Carolina",
  ND: "North Dakota",
  OH: "Ohio",
  OK: "Oklahoma",
  OR: "Oregon",
  PA: "Pennsylvania",
  RI: "Rhode Island",
  SC: "South Carolina",
  SD: "South Dakota",
  TN: "Tennessee",
  TX: "Texas",
  UT: "Utah",
  VT: "Vermont",
  VA: "Virginia",
  WA: "Washington",
  WV: "West Virginia",
  WI: "Wisconsin",
  WY: "Wyoming",
  DC: "District of Columbia",
};

// Available gender options for OrderInfo
const validGenderOptions = ["Male", "Female", "Unspecified"];

const genderAbbrevToFull = { M: "Male", F: "Female" };

/**
 * Normalizes gender: M/F (case-insensitive) → Male/Female; full names matched case-insensitively return canonical option.
 */
const normalizeGender = (value) => {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const upper = trimmed.toUpperCase();
  if (genderAbbrevToFull[upper]) return genderAbbrevToFull[upper];
  const canonical = validGenderOptions.find((opt) => opt.toLowerCase() === trimmed.toLowerCase());
  return canonical ?? null;
};

// Available state values from dropdown
const validStateValues = usaStatesListItems.map((item) => item.value);

/**
 * Normalizes state: full valid name (ignore case) is returned as canonical full name; abbreviation is converted to full name.
 */
const convertStateToFullName = (stateValue) => {
  if (!stateValue) return null;
  const trimmed = stateValue.trim();
  const lower = trimmed.toLowerCase();
  const fullNameMatch = validStateValues.find((v) => v.toLowerCase() === lower);
  if (fullNameMatch) return fullNameMatch;
  const upperAbbrev = trimmed.toUpperCase();
  return stateAbbrevToFullName[upperAbbrev] || null;
};

/**
 * Validates if a value is in the list of valid options
 */
const isValidOption = (value, validOptions) => {
  if (!value) return false;
  return validOptions.includes(value.trim());
};

/**
 * Parses BIN and PCN from insurance provider string and returns cleaned provider name
 * Format: "11 - PBM: Medi-Cal Magellan (MC) | BIN: 022659 | PCN: 6334225"
 * Returns: "11 - PBM: Medi-Cal Magellan (MC)"
 */
const parseInsuranceProviderForBinPcn = (insuranceProvider, orderInfo) => {
  if (!insuranceProvider || typeof insuranceProvider !== "string") {
    return insuranceProvider;
  }

  // Extract the part before the first "|" as the insurance provider name
  const providerName = insuranceProvider.split("|")[0].trim();

  // Regex to match BIN: followed by digits
  const binMatch = insuranceProvider.match(/BIN:\s*(\d+)/i);
  if (binMatch && binMatch[1]) {
    const binValue = binMatch[1].trim();
    if (binValue && !orderInfo.bin) {
      orderInfo.bin = binValue;
    }
  }

  // Regex to match PCN: followed by alphanumeric characters
  const pcnMatch = insuranceProvider.match(/PCN:\s*(\S+)/i);
  if (pcnMatch && pcnMatch[1]) {
    const pcnValue = pcnMatch[1].trim();
    if (pcnValue && !orderInfo.processorControlNumber) {
      orderInfo.processorControlNumber = pcnValue;
    }
  }

  return providerName;
};

/**
 * Looks up medication using NDC or medication name with optional strength/dosage
 * Relies on backend token-based search - sends full query and lets backend handle tokenization
 * Returns the best matching medication or null
 */
const lookupMedication = async (medicationName, ndc, strength = null) => {
  if (!medicationName) {
    return null;
  }

  try {
    let results = [];

    // 1. If NDC is available, lookup by NDC first
    if (ndc && /^\d{11}$/.test(ndc.replace(/-/g, ""))) {
      const cleanNdc = ndc.replace(/-/g, "");
      try {
        results = await mainServices.searchMedications(cleanNdc);
        if (results && results.length > 0) {
          // If we found results by NDC, return the first one
          return results[0];
        }
      } catch (error) {
        // NDC lookup failed, continue to name search
      }
    }

    // 2. Build search query: include medication name and strength if available
    // Backend will split into tokens and use AND logic, so all tokens must match
    const medicationNameQuery = medicationName.trim();
    let searchQuery = medicationNameQuery;
    if (strength) {
      searchQuery = `${searchQuery} ${strength}`.trim();
    }

    // 3. Send full query to backend - backend handles token-based search
    // First try with strength/dosage if available (for precise matching)
    if (searchQuery.length >= 3) {
      try {
        results = await mainServices.searchMedications(searchQuery);
        // Ensure results is an array
        if (!Array.isArray(results)) {
          results = [];
        }
      } catch (error) {
        // Search failed, try without strength
        results = [];
      }
    } else {
      return null;
    }

    // 4. If no results with strength/dosage, try again without it
    // This handles cases where dosage is quantity (e.g., "50 tablets") not strength
    if ((!results || results.length === 0) && strength && medicationNameQuery.length >= 3) {
      try {
        results = await mainServices.searchMedications(medicationNameQuery);
        // Ensure results is an array
        if (!Array.isArray(results)) {
          results = [];
        }
      } catch (error) {
        // Search failed, return null
        return null;
      }
    }

    if (!results || results.length === 0) {
      return null;
    }

    // 5. If one medication is returned, select it
    if (results.length === 1) {
      return results[0];
    }

    // 6. If multiple results, return first one
    // This ensures we use the backend's best match rather than falling back to intake medication
    return results[0];
  } catch (error) {
    // Lookup failed, return null
    return null;
  }
};

/**
 * Prepares OrderInfo object from selected intake data
 * Only maps fields if they exist in source (doesn't overwrite with null)
 * Validates dropdown/radio fields and skips if value doesn't match available options
 */
/**
 * Patient-list row → the OrderInfo patient fields (plus whatever provider/insurance the roster row
 * carries in extraData). Only sets fields the row actually has, never blanks. Shared by the intake
 * prepopulate path and the no-records fallback (handleUsePatientDetails).
 */
const applyPatientRosterData = (patient, orderInfo) => {
  if (patient.firstName) orderInfo.patientFirstName = patient.firstName;
  if (patient.lastName) orderInfo.patientLastName = patient.lastName;
  if (patient.dob) orderInfo.patientDob = patient.dob;
  if (patient.externalId) orderInfo.patientExternalId = patient.externalId;
  if (patient.phoneNumber || patient.extraData?.phoneNumber)
    orderInfo.patientPhone = patient.phoneNumber ? patient.phoneNumber : patient.extraData?.phoneNumber;
  if (patient.address || patient.extraData?.patientAddress1) {
    const addresses = splitAddress(patient.address ? patient.address : patient.extraData?.patientAddress1);
    orderInfo.patientAddress1 = addresses.address1;
    orderInfo.patientAddress2 = [addresses.address2, patient.extraData?.patientAddress2].filter(Boolean).join(", ");
  }
  if (patient.city || patient.extraData?.patientCity) {
    orderInfo.patientCity = patient.city || patient.extraData?.patientCity;
  }
  if (patient.state || patient.extraData?.patientState) {
    const fullStateName = convertStateToFullName(patient.state || patient.extraData?.patientState);
    if (fullStateName) {
      orderInfo.patientState = fullStateName;
    }
  }
  if (patient.zip || patient.extraData?.patientZip) {
    orderInfo.patientZip = patient.zip || patient.extraData?.patientZip;
  }
  // Patient.gender is a real column (roster imports fill it); extraData is the older, per-client spelling.
  const rawGender = patient.gender || patient.extraData?.patientGender;
  if (rawGender) {
    const genderValue = normalizeGender(rawGender);
    if (genderValue) orderInfo.patientGender = genderValue;
  }
  if (patient.extraData?.providerContactFax) orderInfo.providerContactFax = patient.extraData.providerContactFax;
  if (patient.extraData?.providerNpiNumber) orderInfo.providerNpiNumber = patient.extraData.providerNpiNumber;
  if (patient.extraData?.providerZip) orderInfo.providerZip = patient.extraData.providerZip;
  if (patient.extraData?.insuranceProvider) orderInfo.insuranceProvider = patient.extraData.insuranceProvider;
  if (patient.extraData?.providerCity) orderInfo.providerCity = patient.extraData.providerCity;
  if (patient.extraData?.providerLastName) orderInfo.providerLastName = patient.extraData.providerLastName;
  if (patient.extraData?.providerFirstName) orderInfo.providerFirstName = patient.extraData.providerFirstName;
  if (patient.extraData?.providerContactPhone) orderInfo.providerContactPhone = patient.extraData.providerContactPhone;
  if (patient.extraData?.providerState) {
    const fullStateName = convertStateToFullName(patient.extraData.providerState);
    if (isValidOption(fullStateName, validStateValues)) {
      orderInfo.providerState = fullStateName;
    }
  }
  if (patient.extraData?.providerAddress1) orderInfo.providerAddress1 = patient.extraData.providerAddress1;
  if (patient.extraData?.processorControlNumber)
    orderInfo.processorControlNumber = patient.extraData.processorControlNumber;
  if (patient.extraData?.bin) orderInfo.bin = patient.extraData.bin;
  if (patient.extraData?.grpNumber) orderInfo.grpNumber = patient.extraData.grpNumber;
  if (patient.extraData?.patientMemberId) orderInfo.patientMemberId = patient.extraData.patientMemberId;
  if (patient.extraData?.insuranceProvider) orderInfo.insuranceProvider = patient.extraData.insuranceProvider;
};

const prepopulateOrderInfo = async () => {
  const orderInfo = {};

  // Intake-sourced submissions are always CoverMyMeds — set explicitly (not just left as a
  // fallback default) so re-selecting intake data after having picked an imported order
  // (which sets submissionMethod to whatever the backend resolved, e.g. "eFax") doesn't leave
  // that stale — each source's own prepopulation is self-sufficient for this field.
  orderInfo.submissionMethod = "CoverMyMeds";

  // 1. Selected IntakeDocuments → patientChart
  if (intakeRecords.value?.documents && selectedDocuments.value.length > 0) {
    orderInfo.patientChart = intakeRecords.value.documents
      .filter((doc) => selectedDocuments.value.includes(doc.id))
      .map(buildPatientChartEntryFromIntakeDoc)
      .filter(Boolean);
  }

  // 2. Selected Patient → OrderInfo Patient Fields
  if (selectedPatient.value) {
    applyPatientRosterData(selectedPatient.value, orderInfo);
  }

  // 2b. Additional patient charts (multi-select mode)
  if (multiPatientChartEnabled.value && selectedPatients.value.length > 1) {
    orderInfo.additionalPatientExternalIds = selectedPatients.value
      .filter((p) => p.id !== selectedPatient.value?.id && p.externalId)
      .map((p) => ({
        externalId: p.externalId,
        patientId: p.id,
      }));
  }

  // 3. Selected IntakeInsuranceCard → OrderInfo Insurance Fields
  if (intakeRecords.value?.documents) {
    intakeRecords.value.documents.forEach((doc) => {
      const selectedCardId = selectedInsuranceCards.value[doc.id];
      if (selectedCardId && doc.insuranceCards) {
        const card = doc.insuranceCards.find((c) => c.id === selectedCardId);
        if (card) {
          if (card.memberId) orderInfo.patientMemberId = card.memberId;
          if (card.payorName) {
            // Parse BIN and PCN from payorName and get cleaned provider name (first part before "|")
            orderInfo.insuranceProvider = parseInsuranceProviderForBinPcn(card.payorName, orderInfo);
          }
          if (card.insurancePlan) orderInfo.insurancePlanType = card.insurancePlan;
          // Map benefitType: MEDICAL -> medical_benefits, PHARMACY -> pharmacy_benefits
          if (card.benefitType) {
            const benefitTypeMap = {
              MEDICAL: "medical_benefits",
              PHARMACY: "pharmacy_benefits",
            };
            orderInfo.benefitType = Object.keys(benefitTypeMap).includes(card.benefitType)
              ? benefitTypeMap[card.benefitType]
              : "pharmacy_benefits";
          }
          // Only set from card fields if not already parsed from payorName
          if (card.rxBin && !orderInfo.bin) orderInfo.bin = card.rxBin;
          if (card.rxPcn && !orderInfo.processorControlNumber) orderInfo.processorControlNumber = card.rxPcn;
          if (card.rxGrp && !orderInfo.grpNumber) orderInfo.grpNumber = card.rxGrp;
          if (card.Group && !orderInfo.grpNumber) orderInfo.grpNumber = card.Group;
        }
      }
    });
  }

  // Default benefitType to "pharmacy_benefits" if not set or no insurance card selected
  if (!orderInfo.benefitType) {
    orderInfo.benefitType = "pharmacy_benefits";
  }

  // 4. Selected therapyAdministration → OrderInfo Medication Fields
  // 5. Selected IntakeOrderForm (containing medication) → OrderInfo Other Fields
  if (intakeRecords.value?.documents) {
    for (const doc of intakeRecords.value.documents) {
      const selectedMedicationName = selectedMedications.value[doc.id];
      if (selectedMedicationName && doc.orderForms) {
        // Find the order form containing the selected medication
        let selectedOrderForm = null;
        let selectedTherapy = null;

        for (const form of doc.orderForms) {
          if (form.therapyAdministration) {
            selectedTherapy = form.therapyAdministration.find((therapy) => therapy.name === selectedMedicationName);
            if (selectedTherapy) {
              selectedOrderForm = form;
              break;
            }
          }
        }

        // Map medication fields from therapyAdministration
        if (selectedTherapy) {
          const medicationName = selectedTherapy.name;
          const ndc = selectedTherapy.nationalDrugCode || selectedTherapy.ndcNumber;
          // Get strength/dosage for more precise matching
          const strength = selectedTherapy.strength || selectedTherapy.dosage || null;

          // Lookup medication to get standardized medication details
          const lookedUpMedication = await lookupMedication(medicationName, ndc, strength);
          if (lookedUpMedication && lookedUpMedication.medication) {
            // Use looked up medication details - prioritize these over intake values
            orderInfo.medication = lookedUpMedication.medication;
            if (lookedUpMedication.nationalDrugCode) {
              orderInfo.nationalDrugCode = lookedUpMedication.nationalDrugCode;
            }
            if (lookedUpMedication.name) {
              const medicationDefaults = getMedicationDefaultsForName(
                lookedUpMedication.name,
                requirementsStore.clientConfig?.priorAuth?.medicationDefaults
              );
              // Apply medicationDefaults from client config when medication name matches
              if (medicationDefaults) {
                applyMedicationDefaultsToTarget(medicationDefaults, orderInfo);
              }
            }
          } else if (medicationName) {
            // Fallback to original medication name only if lookup completely failed
            orderInfo.medication = medicationName;
          }

          if (selectedTherapy.dosage) {
            orderInfo.dailyDosage = selectedTherapy.dosage;
          } else if (selectedTherapy.dailyDosage) {
            orderInfo.dailyDosage = selectedTherapy.dailyDosage;
          }
          if (selectedTherapy.nationalDrugCode && !orderInfo.nationalDrugCode) {
            orderInfo.nationalDrugCode = selectedTherapy.nationalDrugCode;
          } else if (selectedTherapy.ndcNumber && !orderInfo.nationalDrugCode) {
            orderInfo.nationalDrugCode = selectedTherapy.ndcNumber;
          }

          // Frequency: frequencyValue/frequencyUnit take precedence
          if (selectedTherapy.frequencyValue && selectedTherapy.frequencyUnit) {
            orderInfo.frequency = `${selectedTherapy.frequencyValue} ${selectedTherapy.frequencyUnit}`;
          } else if (selectedTherapy.frequency) {
            orderInfo.frequency = selectedTherapy.frequency;
          }

          // Expected length of therapy
          if (selectedTherapy.expectedLengthOfTherapy) {
            orderInfo.expectedLengthOfTherapy = selectedTherapy.expectedLengthOfTherapy;
          } else if (selectedTherapy.durationValue && selectedTherapy.durationUnit) {
            orderInfo.expectedLengthOfTherapy = `${selectedTherapy.durationValue} ${selectedTherapy.durationUnit}`;
          }

          if (selectedTherapy.quantityPer30Days) {
            orderInfo.quantityPer30Days = selectedTherapy.quantityPer30Days;
          }
        }

        // Map order form fields
        if (selectedOrderForm) {
          // Provider fields
          if (selectedOrderForm.providerFirstName) orderInfo.providerFirstName = selectedOrderForm.providerFirstName;
          if (selectedOrderForm.providerLastName) orderInfo.providerLastName = selectedOrderForm.providerLastName;
          if (selectedOrderForm.providerTitle) orderInfo.providerTitle = selectedOrderForm.providerTitle;
          if (selectedOrderForm.providerNpi) orderInfo.providerNpiNumber = selectedOrderForm.providerNpi;

          // Remove extension from phone/fax
          if (selectedOrderForm.providerFax) {
            orderInfo.providerContactFax = selectedOrderForm.providerFax.replace(/\s*ext\.?\s*\d+$/i, "").trim();
          }
          if (selectedOrderForm.providerPhone) {
            orderInfo.providerContactPhone = selectedOrderForm.providerPhone.replace(/\s*ext\.?\s*\d+$/i, "").trim();
          }

          if (selectedOrderForm.providerAddress) orderInfo.providerAddress1 = selectedOrderForm.providerAddress;
          if (selectedOrderForm.providerCity) orderInfo.providerCity = selectedOrderForm.providerCity;
          if (selectedOrderForm.providerState) {
            const fullStateName = convertStateToFullName(selectedOrderForm.providerState);
            if (fullStateName && isValidOption(fullStateName, validStateValues)) {
              orderInfo.providerState = fullStateName;
            }
          }
          if (selectedOrderForm.providerZip) orderInfo.providerZip = selectedOrderForm.providerZip;
          if (selectedOrderForm.providerPracticeName)
            orderInfo.providerOfficeName = selectedOrderForm.providerPracticeName;

          // Patient demographics from order form
          if (selectedOrderForm.patientGender) {
            if (isValidOption(selectedOrderForm.patientGender, validGenderOptions)) {
              orderInfo.patientGender = selectedOrderForm.patientGender.trim();
            }
            // Skip if gender doesn't match available options
          }
          if (selectedOrderForm.patientAddress) {
            const addresses = splitAddress(selectedOrderForm.patientAddress);
            orderInfo.patientAddress1 = addresses.address1;
            orderInfo.patientAddress2 = addresses.address2;
          }
          if (selectedOrderForm.patientCity) orderInfo.patientCity = selectedOrderForm.patientCity;
          if (selectedOrderForm.patientState) {
            const fullStateName = convertStateToFullName(selectedOrderForm.patientState);
            if (fullStateName && isValidOption(fullStateName, validStateValues)) {
              orderInfo.patientState = fullStateName;
            }
          }
          if (selectedOrderForm.patientZip) orderInfo.patientZip = selectedOrderForm.patientZip;

          // Combine weight value and unit into string
          if (selectedOrderForm.patientWeightValue || selectedOrderForm.patientWeightUnit) {
            const weightParts = [];
            if (selectedOrderForm.patientWeightValue) weightParts.push(selectedOrderForm.patientWeightValue);
            if (selectedOrderForm.patientWeightUnit) weightParts.push(selectedOrderForm.patientWeightUnit);
            orderInfo.patientWeight = weightParts.join(" ");
          }

          if (selectedOrderForm.patientAllergies) orderInfo.patientDrugAllergies = selectedOrderForm.patientAllergies;
          if (selectedOrderForm.diagnosisIcd10Code) {
            orderInfo.primaryDiagnosisIcd10Code = selectedOrderForm.diagnosisIcd10Code;
            // Fetch diagnosis name from ICD10 code
            const diagnosisName = await fetchDiagnosisNameFromICD10(selectedOrderForm.diagnosisIcd10Code);
            if (diagnosisName) {
              orderInfo.primaryDiagnosisName = diagnosisName;
            }
          }
          if (selectedOrderForm.dateOfOrder) orderInfo.treatmentStartDate = selectedOrderForm.dateOfOrder;
        }
      }
    }
  }

  return orderInfo;
};

const CONTRACTIONS = {
  street: "st",
  avenue: "ave",
  boulevard: "blvd",
  drive: "dr",
  lane: "ln",
  parkway: "pkwy",
  place: "pl",
  road: "rd",
  square: "sq",
  west: "w",
  east: "e",
  north: "n",
  south: "s",
};
const MAX_ADDRESS_LENGTH = 35;
/** Splits address into two lines if it's longer than 35 characters */
const splitAddress = (address) => {
  if (address.length <= MAX_ADDRESS_LENGTH) {
    return { address1: address, address2: "" };
  }

  const separators = " ,.";
  let contracted = address;
  /** Contract the address in case it's longer than MAX_ADDRESS_LENGTH
   * so we have more space for the main address to be visible on Line 1 */
  for (const contraction of Object.keys(CONTRACTIONS)) {
    contracted = contracted.replace(RegExp(`\\b${contraction}\\b`, "ig"), CONTRACTIONS[contraction]);
  }
  const matches = [...contracted.matchAll(new RegExp(`[${separators}]`, "g"))];
  const splitIndex = matches.reduce((acc, match) => {
    if (match.index < MAX_ADDRESS_LENGTH) {
      return match.index;
    }
    return acc;
  }, MAX_ADDRESS_LENGTH);
  return {
    address1: contracted.slice(0, splitIndex),
    address2: contracted.slice(splitIndex + 1),
  };
};

/**
 * Fallback for a patient with no imported order and no intake documents: start the form from what
 * the patient list already knows — demographics, plus the insurance card picked above (a lone card
 * is pre-selected; none is fine too). Same event as the other two sources, so the parent's
 * clear-then-apply handling (and patient-switch detection) is identical.
 */
const usingPatientDetails = ref(false);
const handleUsePatientDetails = async () => {
  const patient = selectedPatient.value;
  if (!patient || usingPatientDetails.value) return;
  usingPatientDetails.value = true;
  try {
    // Same explicit default as the intake path — see prepopulateOrderInfo.
    const orderInfo = { submissionMethod: "CoverMyMeds" };
    applyPatientRosterData(patient, orderInfo);
    if (selectedPatientInsurance.value) {
      insuranceCardOrderFields(selectedPatientInsurance.value).forEach(([field, value]) => {
        orderInfo[field] = value;
      });
    }
    if (!orderInfo.benefitType) {
      orderInfo.benefitType = "pharmacy_benefits";
    }
    emit("prepopulate-order-info", orderInfo, selectedPatientId.value);
    clearSelections();
  } finally {
    usingPatientDetails.value = false;
  }
};

const handlePrepopulate = async () => {
  const prepopulatedData = await prepopulateOrderInfo();
  // Second arg: see handleUseImportedOrder — lets the handler keep user-entered medication
  // queues on a same-patient re-prepopulate instead of silently wiping them.
  emit("prepopulate-order-info", prepopulatedData, selectedPatientId.value);
  clearSelections();
};

// Watch for patient selection changes and fetch intake records + imported order data
watch(selectedPatientId, (newPatientId) => {
  if (newPatientId) {
    fetchIntakeRecords(newPatientId);
    fetchImportedOrders(newPatientId);
    fetchPatientInsurances(newPatientId);
  } else {
    intakeRecords.value = null;
    importedOrders.value = [];
    patientInsurances.value = [];
    selectedImportRowId.value = null;
    submissionSource.value = null;
    clearSelections();
  }
});

defineExpose({
  selectedPatient,
  selectedPatients,
  selectedDocuments,
  selectedInsuranceCards,
  selectedMedications,
  intakeRecords,
  importedOrders,
  clearSelection,
  resetSearch,
  prepopulateOrderInfo,
});
</script>

<style lang="scss" scoped></style>
